// Auto-Insights design tokens — MIRROR of backend/app/insights/design_system.py.
// Single source of truth for the executive board's look, so the in-app Recharts render and
// the exported PPTX/living-link are visually identical (Requirement 7.5, Property 10).
//
// KEEP IN SYNC with design_system.py. scripts/smoke_design_system.py asserts the PALETTE,
// SEMANTIC, and ORDINAL values here match the Python source (drift guard). If you change one
// side, change both. (Platform default palette = CFO executive palette — D1 rev / R23/R26.
// A customer brand theme overrides it per-customer; until then everyone gets the CFO look.)

export const PALETTE = ["#079AFB", "#797978", "#1E50C8", "#E56F0F", "#03811E", "#E13718", "#0891B2", "#7C3AED"];

export const SEMANTIC = { positive: "#03811E", negative: "#E13718", neutral: "#64748B", target: "#E56F0F" };

// Role colors (R23) — default = CFO executive palette; a customer brand theme may override
// primary/comparison/target/threshold. The renderer maps a series' resolved role
// (chart_spec.series_roles) to these.
export const ROLES = { primary: "#079AFB", comparison: "#797978", target: "#E56F0F", threshold: "#E13718" };

// KPI card semantic tinting (R24) — fixed/hue-locked; applied by a deterministic rule when a
// KPI has a target/baseline to compare against (else neutral).
export const KPI_TINT = {
  positive: { fill: "#E6F4EA", border: "#BBF7D0", text: "#166534", dot: "#03811E" },
  negative: { fill: "#FCE8E6", border: "#FECACA", text: "#991B1B", dot: "#E13718" },
  neutral: { fill: "#F8FAFC", border: "#E2E8F0", text: "#0F172A", dot: "#079AFB" },
};

// Pinned Table style — MIRROR of design_system.py's TABLE_STYLE (roadmap: "the table is too
// simple, make it more visually appealing" — same look also required in the PPTX/XLSX export,
// Requirement 7.5/Property 10). Drift-guarded by scripts/smoke_design_system.py exactly like
// PALETTE/SEMANTIC/ROLES above.
export const TABLE_STYLE = {
  header_bg: "#079AFB",
  header_text: "#FFFFFF",
  border: "#E2E8F0",
  band_bg: "#F8FAFC",
  row_bg: "#FFFFFF",
  total_bg: "#EFF6FF",
  total_text: "#0F172A",
  total_border: "#079AFB",
  text: "#334155",
};

// Row-label text patterns marking a spreadsheet row as a Total/Grand-Total/Subtotal row —
// mirrors design_system.py's _TOTAL_ROW_PATTERNS/is_total_row_label exactly (same match rule:
// the WHOLE trimmed, lowercased label, never a substring).
const _TOTAL_ROW_PATTERNS = new Set([
  "total general", "grand total", "gran total", "total", "subtotal", "sub total", "totales",
]);

export function isTotalRowLabel(text) {
  if (typeof text !== "string") return false;
  return _TOTAL_ROW_PATTERNS.has(text.trim().toLowerCase());
}

export const TYPE = { inAppFont: "Inter", exportFont: "Calibri", titlePt: 18, valuePt: 12, axisPt: 11, narrativePt: 12 };

export const LAYOUT = {
  background: "#ffffff",
  minimalGridlines: true,
  chartBorder: false,
  centeredTitle: true,
  pairSideBySide: true,
  boardVisualCap: 6,
  topNCategories: 8,
  briefItems: [3, 5],
};

export const ORDINAL = {
  age_band: ["Under 18", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"],
  month: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  weekday: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  size: ["XS", "S", "M", "L", "XL", "XXL"],
  quarter: ["Q1", "Q2", "Q3", "Q4"],
};

// The executive "mode" chip shown when auto_insights_board is ON (Requirement 21.3).
export const MODE_INDICATOR = { label: "Auto-Insights", accent: "#6366f1" };
